import { ExamTemplate } from '@gmm/sdk-voodoo/exercises';
import { ProblemAndAnswer } from '@gmm/sdk-voodoo/shared';
import create from 'zustand';
import { devtools } from 'zustand/middleware';
import { generateUnsavedExamIdentifier } from '../../utils';
import { LockedExamProblemParams } from '~/lib/problemPreview/hooks/useLockedExamTemplateProblem';

interface ExamTemplateProblem {
  skillId: string;
  positionWithinSkill: number;
  problemAndAnswer: ProblemAndAnswer;
  canRevert: boolean;
  dirty: boolean;
}

interface ExamTemplateState {
  problemsMap: Map<string, ExamTemplateProblem[]>;

  getNextPositionWithinSkill: (skillId: string) => number;
  isServerDirty: () => boolean;
  isDirtyInstance: (skillId: string, positionWithinSkill: number) => boolean;
  isDirtySkill: (skillId: string) => boolean;
  addExamTemplateProblem: (
    lockedExamIdentifier: string,
    problem: ExamTemplateProblem,
  ) => void;
  getExamTemplateProblem: (
    params: LockedExamProblemParams,
  ) => ExamTemplateProblem | undefined;
  clearLockedProblems: () => void;
  getProblemsForCurrentExam: () => ExamTemplateProblem[];

  examTemplate: ExamTemplate | undefined;
  setExamTemplate: (examTemplate: ExamTemplate | undefined) => void;
  unsavedIdentifier: string | undefined;
  getUnsavedIdentifier: () => string;
  getExamIdentifier: () => string | undefined;
  clearUnsavedIdentifier: () => void;
}

export const useExamTemplateStore = create<ExamTemplateState>()(
  devtools(
    (set, get) => ({
      problemsMap: new Map(),
      unsavedIdentifier: undefined,
      examTemplate: undefined,

      getUnsavedIdentifier: () => {
        let ret = get().unsavedIdentifier;
        if (ret === undefined) {
          ret = generateUnsavedExamIdentifier();
          set({ unsavedIdentifier: ret });
        }
        return ret;
      },

      clearUnsavedIdentifier: () => {
        set({ unsavedIdentifier: undefined });
      },

      setExamTemplate: (examTemplate: ExamTemplate | undefined) => {
        set({ examTemplate });
      },

      getExamIdentifier: () => {
        const { unsavedIdentifier, examTemplate } = get();
        return examTemplate && examTemplate.id
          ? examTemplate.id.toString()
          : unsavedIdentifier;
      },

      getNextPositionWithinSkill: (skillId: string) => {
        return (
          get().examTemplate?.skills.find(item => item.type === skillId)
            ?.instances ?? 0
        );
      },

      isServerDirty: () => {
        const problems = get().getProblemsForCurrentExam();
        return problems.some(item => item.dirty);
      },

      isDirtyInstance: (skillId: string, positionWithinSkill: number) => {
        const problems = get().getProblemsForCurrentExam();
        return problems.some(
          item =>
            item.skillId === skillId &&
            item.positionWithinSkill === positionWithinSkill &&
            item.dirty,
        );
      },

      isDirtySkill: (skillId: string) => {
        const problems = get().getProblemsForCurrentExam();
        return problems.some(item => item.skillId === skillId && item.dirty);
      },

      addExamTemplateProblem: (
        lockedExamIdentifier: string,
        problem: ExamTemplateProblem,
      ) => {
        set(state => {
          const current = new Map(state.problemsMap);
          const existingList = current.get(lockedExamIdentifier) ?? [];

          const index = existingList.findIndex(
            item =>
              item.skillId === problem.skillId &&
              item.positionWithinSkill === problem.positionWithinSkill,
          );

          const updatedList =
            index >= 0
              ? existingList.map((item, i) => (i === index ? problem : item))
              : [...existingList, problem];

          current.set(lockedExamIdentifier, updatedList);
          return { problemsMap: current };
        });
      },

      getExamTemplateProblem: (params: LockedExamProblemParams) => {
        const { lockedExamIdentifier, skillId, positionWithinSkill } = params;
        const map = get().problemsMap;
        const problems = map.get(lockedExamIdentifier) ?? [];
        return problems.find(
          item =>
            item.skillId === skillId &&
            item.positionWithinSkill === positionWithinSkill,
        );
      },

      clearLockedProblems: () => {
        const map = get().problemsMap;
        if (map.size === 0) return;
        set({ problemsMap: new Map() });
      },

      getProblemsForCurrentExam: (): ExamTemplateProblem[] => {
        const key = get().getExamIdentifier();
        return key ? get().problemsMap.get(key) ?? [] : [];
      },
    }),
    { name: 'ExamTemplateProblemStore' },
  ),
);
